import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { api, friendlyError } from '../lib/api-client';
import { formatDate } from '../i18n/formatters';
import {
  PartnerCommissionsResponse,
  PartnerDashboardResponse,
  commissionStatusBadgeVariant,
  commissionStatusLabel,
  formatPartnerMoney,
} from '../lib/partner-portal';
import Container from '../components/ui/Container';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import AlertBanner from '../components/ui/AlertBanner';
import EmptyState from '../components/ui/EmptyState';
import {
  PartnerHeaderMeta,
  PartnerPagination,
  portalTableCellStyle,
  portalTableHeaderStyle,
} from '../components/partner-portal/shared';

function formatMetricAmount(value: number): string {
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export default function PartnerCommissionsPage() {
  const { t } = useTranslation(['partnerPortal', 'common']);
  const { partnerAccess } = useAuth();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PartnerCommissionsResponse | null>(null);
  const [summary, setSummary] = useState<PartnerDashboardResponse['dashboard']['metrics'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await api.get<PartnerCommissionsResponse>(`/partner/commissions?page=${page}&pageSize=25`);
        setData(response);
      } catch (err) {
        setError(friendlyError(err, t('errors.load')));
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [page, t]);

  useEffect(() => {
    const loadSummary = async () => {
      try {
        const dashboard = await api.get<PartnerDashboardResponse>('/partner/dashboard');
        setSummary(dashboard.dashboard.metrics);
      } catch {
        // Non-blocking summary panel.
      }
    };

    void loadSummary();
  }, []);

  return (
    <Container maxWidth={1320}>
      <div style={{ display: 'grid', gap: 20 }}>
        <PageHeader
          eyebrow={t('commissions.eyebrow')}
          title={t('commissions.title')}
          subtitle={partnerAccess?.partnerName}
          description={t('commissions.description')}
          meta={partnerAccess ? <PartnerHeaderMeta access={partnerAccess} /> : undefined}
        />

        {error && <AlertBanner variant="danger">{error}</AlertBanner>}

        {summary && (
          <Card
            style={{
              border: '1px solid rgba(20, 200, 198, 0.22)',
              background: 'var(--card-bg)',
            }}
          >
            <div style={{ display: 'grid', gap: 18 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--acc-color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {t('commissions.summaryEyebrow')}
                </div>
                <div style={{ marginTop: 8, fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--acc-color-text)' }}>
                  {formatMetricAmount(summary.totalCommissionEarned)}
                </div>
                <div style={{ marginTop: 6, fontSize: 13, color: 'var(--acc-color-text-muted)', lineHeight: 1.65 }}>
                  {t('commissions.summaryDescription')}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--acc-color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {t('cards.approvedCommission')}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 20, fontWeight: 760, color: 'var(--acc-color-text)' }}>
                    {formatMetricAmount(summary.approvedCommission)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--acc-color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {t('cards.paidCommission')}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 20, fontWeight: 760, color: 'var(--acc-color-text)' }}>
                    {formatMetricAmount(summary.paidCommission)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--acc-color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {t('cards.pendingCommission')}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 20, fontWeight: 760, color: 'var(--acc-color-text)' }}>
                    {formatMetricAmount(summary.pendingCommission)}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        )}

        <Card>
          {loading ? (
            <div>{t('common:actions.loading')}</div>
          ) : !data || data.commissions.length === 0 ? (
            <EmptyState
              title={t('empty.commissionsTitle')}
              description={t('empty.commissionsDescription')}
              dashed
            />
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={portalTableHeaderStyle}>{t('fields.billingPeriod')}</th>
                    <th style={portalTableHeaderStyle}>{t('fields.organisation')}</th>
                    <th style={portalTableHeaderStyle}>{t('fields.revenue')}</th>
                    <th style={portalTableHeaderStyle}>{t('fields.rate')}</th>
                    <th style={portalTableHeaderStyle}>{t('fields.amount')}</th>
                    <th style={portalTableHeaderStyle}>{t('fields.status')}</th>
                    <th style={portalTableHeaderStyle}>{t('fields.invoice')}</th>
                    <th style={portalTableHeaderStyle}>{t('fields.created')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.commissions.map((commission) => (
                    <tr key={commission.id}>
                      <td style={portalTableCellStyle}>
                        {formatDate(commission.billingPeriodStart)} - {formatDate(commission.billingPeriodEnd)}
                      </td>
                      <td style={portalTableCellStyle}>
                        <div>{commission.organisationName ?? t('common:labels.notAvailable')}</div>
                        <div style={{ color: 'var(--acc-color-text-muted)', marginTop: 4 }}>
                          {commission.organisationSlug ?? ''}
                        </div>
                      </td>
                      <td style={portalTableCellStyle}>{formatPartnerMoney(commission.currency, commission.revenueAmount)}</td>
                      <td style={portalTableCellStyle}>{commission.commissionRate}%</td>
                      <td style={portalTableCellStyle}>{formatPartnerMoney(commission.currency, commission.commissionAmount)}</td>
                      <td style={portalTableCellStyle}>
                        <Badge variant={commissionStatusBadgeVariant(commission.commissionStatus)}>
                          {commissionStatusLabel(commission.commissionStatus, t)}
                        </Badge>
                      </td>
                      <td style={portalTableCellStyle}>{commission.sourceInvoiceId ?? t('common:labels.notRecordedYet')}</td>
                      <td style={portalTableCellStyle}>{formatDate(commission.createdAt) || t('common:labels.notAvailable')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <PartnerPagination
                page={data.page}
                pageSize={data.pageSize}
                total={data.total}
                onPageChange={setPage}
              />
            </div>
          )}
        </Card>
      </div>
    </Container>
  );
}
